/**
 * Reusable Web Worker watchdog — heartbeat-based silent-hang detection.
 *
 * Solves the same problem as the mp3-encoder watchdog (which this module
 * is extracted from): on low-RAM Android devices the OS sometimes
 * zombiefies a Worker mid-execution. The runtime is dead but the page
 * Worker handle survives — postMessage is silently dropped, onerror
 * never fires, the encode/process Promise hangs forever, the user sees
 * "stuck at X% then reset", the feedback widget submits with
 * `last_error: null`, and the bug is invisible to telemetry.
 *
 * The cure: declare every Worker job sends a "heartbeat" (any message
 * — progress / done / error) at sub-second cadence. A polling watchdog
 * detects entries with no heartbeat in N seconds, terminates the worker,
 * rejects pending Promises with a typed `WORKER_HUNG` error, and emits
 * `tool_error{error_type:'<name>_worker_hung'}` telemetry.
 *
 * Usage (mp3-encoder, deepfilter, rubberband, etc.):
 *
 *     var wd = window.WorkerWatchdog.create({
 *         name: 'mp3-encoder',
 *         getPending: function(){ return _pending; },
 *         terminateWorker: function(){
 *             try { if (_worker) _worker.terminate(); } catch(_){}
 *             _worker = null;
 *         },
 *         getHeartbeatTimeoutMs: function(){
 *             return Number(window.__MP3_WORKER_TIMEOUT_OVERRIDE) || 30000;
 *         },
 *         getTickMs: function(){
 *             return Number(window.__MP3_WATCHDOG_TICK_OVERRIDE) || 5000;
 *         },
 *         // STRONGLY RECOMMENDED — see "Ask before convicting" below.
 *         pingWorker: function(id){
 *             try { if (_worker) _worker.postMessage({ id: id, type: '__wdPing' }); } catch(_){}
 *         },
 *     });
 *
 *     // …and in the worker, one line in its message dispatcher:
 *     case '__wdPing': self.postMessage({ id: m.id, type: 'heartbeat', pong: 1 }); break;
 *
 * ── Ask before convicting (2026-08-31) ────────────────────────────────────
 * Listening for spontaneous chatter means every stretch nobody thought to
 * instrument reads as death. `pingWorker` turns "hasn't spoken in N seconds"
 * into "did not answer a direct question", which is what this guard actually
 * wants to know: a worker awaiting I/O has a free message loop and answers
 * instantly, while an OS-zombied or synchronously-wedged one cannot answer at
 * all — the exact case the guard exists for (bug #609).
 *
 * Measured before adding it (7 days): deepfilter 103 kills / 98 sessions and
 * mp3-encoder 76 / 42 dominate; archive 7/5, signalsmith 6/6, supertonic 2/1,
 * basic-pitch 1/1, a2n 1/1 are noise. deepfilter is wired here; mp3-encoder
 * carries its own older copy of this watchdog and was fixed worker-side the
 * same day. The remaining consumers are deliberately NOT wired — 17 events in
 * a week does not justify touching five integrations — but new ones should
 * pass `pingWorker` from the start.
 *
 * Omitting it is safe and byte-identical to the pre-2026-08-31 behaviour, so
 * adoption is per-worker and cannot regress the others. The emitted
 * `tool_error` carries `pinged`: null = no ping handler, 0 = asked and silent.
 *
 *     // When enqueueing a job:
 *     _pending.set(id, { resolve, reject, ... });
 *     wd.mark(id);                  // start tracking heartbeat for id
 *
 *     // In worker.onmessage (any message — progress/done/error):
 *     wd.touch(msg.id);             // reset the heartbeat
 *
 *     // When a job resolves or rejects normally (worker still alive):
 *     wd.forget(id);                // stop tracking; auto-stops poll if last
 *
 *     // When the worker is replaced or terminated externally:
 *     wd.dispose();                 // clear all tracked + stop poll
 *
 * Test hooks: getters are read fresh each tick, so tests can flip
 * `window.__<NAME>_WORKER_TIMEOUT_OVERRIDE` to drive sub-second timeouts.
 */
(function () {
    'use strict';

    function create(opts) {
        if (!opts || !opts.name || typeof opts.getPending !== 'function'
            || typeof opts.terminateWorker !== 'function') {
            throw new Error('WorkerWatchdog.create: { name, getPending, terminateWorker } required');
        }
        var name = opts.name;
        var getHeartbeatTimeoutMs = opts.getHeartbeatTimeoutMs || function () { return 30000; };
        var getTickMs = opts.getTickMs || function () { return 5000; };

        /* Во сколько раз дольше таймаута молчит задание, прежде чем мы
           признаем, что живой воркер его потерял (см. ветку alive-hold). */
        var STUCK_MULTIPLIER = opts.stuckMultiplier || 6;
        var heartbeats = new Map();
        var timer = null;
        /* Пока не 0 — сторож задал вопрос и ждёт ответа до этого момента.
           Сбрасывается любым сообщением воркера (`touch`), потому что ответ
           и есть доказательство жизни — неважно, пинг это или обычный кадр. */
        var pingAwaitingUntil = 0;

        function start() {
            if (timer) return;
            timer = setInterval(check, getTickMs());
        }
        function stop() {
            if (!timer) return;
            clearInterval(timer);
            timer = null;
        }

        function check() {
            var pending = opts.getPending();
            if (!pending || pending.size === 0 || heartbeats.size === 0) {
                stop();
                return;
            }

            var now = Date.now();
            var timeoutMs = getHeartbeatTimeoutMs();
            var hung = [];
            var newestBeat = 0;
            heartbeats.forEach(function (lastBeat, id) {
                if (lastBeat > newestBeat) newestBeat = lastBeat;
                if (now - lastBeat > timeoutMs) {
                    hung.push({ id: id, lagMs: now - lastBeat });
                }
            });
            if (hung.length === 0) return;

            /* ⚠️⚠️ Признак жизни — ПО ВОРКЕРУ, а не по заданию.
               Этот сторож ищет ровно одно: воркер, которого убила ОС
               (postMessage молча проглатывается, onerror не приходит). Любое
               сообщение оттуда доказывает, что воркер жив, — а значит убивать
               нечего, чей бы секундомер ни истёк.

               Почему прежнее правило («истёк секундомер ЛЮБОГО задания →
               терминировать») ошибочно: воркер однопоточный. Пока он молотит
               задание A, задание B стоит в очереди сообщений и физически НЕ
               МОЖЕТ прислать ни одного удара — а секундомер ему завели в
               момент постановки в очередь. Через 30 с сторож объявлял мёртвым
               воркер, который всё это время исправно отвечал по A, и убивал
               ЗДОРОВОЕ задание с формулировкой «peer job hung».

               Замер прода за 30 дней (2026-08-24): 789 таких убийств в восьми
               тулах — denoise 327, pitch 226, audio-enhancer 78,
               archive-extractor 69, harmony-generator 53, vocal-remover 21,
               volume 9, reverse 6. В /audio-enhancer на каждый НАСТОЯЩИЙ
               зависон приходилось ДВА невиновных (76 против 38).

               Случай «воркер жив, но одно задание вправду заклинило» на
               однопоточном воркере не существует: заклинившее задание держит
               цикл сообщений, и стучать некому — это ловится веткой ниже. */
            if (now - newestBeat <= timeoutMs) {
                /* Воркер жив — но «живой воркер» не должен превращаться в
                   вечный спиннер, если он вправду забыл про одно задание.
                   Поэтому у удержания есть потолок: молчание дольше
                   STUCK_MULTIPLIER × таймаута отклоняет ОДНО это задание и
                   НЕ трогает воркер и соседей. Так сохраняется и свойство
                   «нет бесконечного ожидания», ради которого сторож писался,
                   и здоровые соседи, которых он убивал. */
                var stuckAfter = timeoutMs * STUCK_MULTIPLIER;
                var stuck = hung.filter(function (h) { return h.lagMs > stuckAfter; });
                try {
                    if (typeof window.__ae === 'function') {
                        window.__ae('feature_use', {
                            f: 'worker_watchdog_alive_hold',
                            name: name,
                            silent_jobs: hung.length,
                            worst_lag_ms: hung[0].lagMs,
                            newest_beat_ms: now - newestBeat,
                            pending_jobs: pending.size,
                            stuck_jobs: stuck.length,
                        });
                    }
                } catch (_) {}
                stuck.forEach(function (h) {
                    var p = pending.get(h.id);
                    heartbeats.delete(h.id);
                    if (!p) return;
                    pending.delete(h.id);
                    var err = new Error(name + ': job abandoned by a live worker ('
                                        + h.lagMs + 'ms no heartbeat)');
                    err.code = 'WORKER_HUNG';
                    err.heartbeatLagMs = h.lagMs;
                    try { p.reject(err); } catch (_) {}
                });
                if (heartbeats.size === 0) stop();
                return;
            }

            /* ⚠️⚠️ ПЕРЕД приговором — СПРОСИТЬ.
               Всё, что сторож умел до сих пор, — слушать, говорит ли воркер сам.
               Значит любой отрезок, который никто не догадался
               проинструментировать, читается как смерть. Это не гипотеза:
               `deepfilter-worker.js::handleInit` шлёт `progress{ratio:0}`, потом
               делает ЧЕТЫРЕ await подряд без единого сообщения — импорт df.js,
               fetch df_bg.wasm, `await resp.arrayBuffer()` и instantiate.
               ⚠️ Замер (поправка к первой редакции этого комментария): на
               проводе wasm 2.03 МБ (Brotli), 8.97 МБ — это уже РАСПАКОВАННЫЙ
               размер, и `Content-Length` у него НЕТ вовсе. Значит приём модели
               (7.98 МБ, есть Content-Length, качается ридером с долей
               received/total) для wasm ВОСПРОИЗВЕСТИ НЕЛЬЗЯ — делить не на что;
               «качать wasm с прогрессом» отпадает по существу, а не по
               приоритету. Тяжесть отрезка даёт не только сеть, но и
               instantiate 8.97 МБ (замер: 1834 мс при CPU ×1 → 3217 мс при ×8). Воркер всё это время жив и просто молчит.
               Замер: 103 убийства / 98 сессий за 7 дней (denoise 33,
               audio-enhancer 31, voice-enhancer 29, speech-restorer 5), lag
               кучно 30.0–35.0 с, т.е. сторож бьёт при первой возможности.
               Воспроизведено: задержать ответ на df_bg.wasm на 35 с →
               `deepfilter_worker_hung:34992` на ЗДОРОВОМ воркере.

               Поэтому теперь молчание — это повод задать вопрос, а не вердикт:
               шлём пинг и даём один грейс. Живой воркер (даже качающий 9 МБ)
               свободен в цикле сообщений и ответит; заклиненный или убитый ОС —
               не ответит ни при каких условиях, и это ровно тот случай, ради
               которого сторож писался.

               Совместимость: без `opts.pingWorker` поведение НЕ меняется ни на
               байт — воркер без обработчика пинга судится ровно как раньше.
               Поэтому раскатывать по воркерам можно поштучно, а не разом. */
            if (typeof opts.pingWorker === 'function' && !pingAwaitingUntil) {
                pingAwaitingUntil = now + (opts.pingGraceMs || getTickMs() * 2);
                try { opts.pingWorker(hung[0].id); } catch (_) {}
                try {
                    if (typeof window.__ae === 'function') {
                        window.__ae('feature_use', {
                            f: 'worker_watchdog_ping',
                            name: name, lag_ms: hung[0].lagMs, pending_jobs: pending.size,
                        });
                    }
                } catch (_) {}
                return;                       // verdict deferred until the grace expires
            }
            if (pingAwaitingUntil && now < pingAwaitingUntil) return;   // still waiting
            var answered = pingAwaitingUntil ? 0 : null;
            pingAwaitingUntil = 0;

            try { opts.terminateWorker(); } catch (_) {}

            try {
                if (typeof window.__ae === 'function') {
                    window.__ae('tool_error', {
                        error_type: name + '_worker_hung',
                        msg: name + ': worker silent for ' + hung[0].lagMs + 'ms',
                        lag_ms: hung[0].lagMs,
                        pending_jobs: pending.size,
                        // null = this worker has no ping handler (old behaviour);
                        // 0 = it was asked directly and did not answer.
                        pinged: answered,
                    });
                }
            } catch (_) {}

            var hungIds = new Set(hung.map(function (h) { return h.id; }));
            pending.forEach(function (p, id) {
                var info = hungIds.has(id) ? hung.find(function (h) { return h.id === id; }) : null;
                var err = new Error(info
                    ? name + ': worker hung (' + info.lagMs + 'ms no heartbeat)'
                    : name + ': worker terminated (peer job hung)'
                );
                err.code = 'WORKER_HUNG';
                err.heartbeatLagMs = info ? info.lagMs : null;
                try { p.reject(err); } catch (_) {}
            });
            pending.clear();
            heartbeats.clear();
            stop();
        }

        return {
            mark: function (id) {
                heartbeats.set(id, Date.now());
                start();
            },
            touch: function (id) {
                /* Любое сообщение снимает начатый допрос — воркер ответил.
                   Сбрасываем ДО проверки `heartbeats.has`: пинг мог уйти по
                   одному заданию, а ответить воркер может по другому, и это
                   всё равно доказывает, что он жив. */
                pingAwaitingUntil = 0;
                if (heartbeats.has(id)) heartbeats.set(id, Date.now());
            },
            forget: function (id) {
                heartbeats.delete(id);
                if (heartbeats.size === 0) stop();
            },
            dispose: function () {
                heartbeats.clear();
                stop();
            },
            // For tests / introspection
            tracked: function () { return heartbeats.size; },
        };
    }

    window.WorkerWatchdog = { create: create };
})();
